import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Eye, Lock, Database, UserCheck, Phone, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-900 to-black py-12 sm:py-16">
          <div className="container mx-auto px-4 text-center">
            <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Privacy <span className="text-yellow-500">Policy</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
              Your privacy is important to us. Learn how we protect your data.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-8 sm:py-12">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10">
            
            {/* Last Updated */}
            <p className="text-gray-500 text-sm mb-8">Last Updated: January 2025</p>

            {/* Introduction */}
            <section className="mb-8">
              <p className="text-gray-700 leading-relaxed">
                <strong>Jain Watch Company</strong> ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or make a purchase. Please read this policy carefully to understand our practices regarding your personal data.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Database className="w-6 h-6 text-yellow-500 mr-2" />
                Information We Collect
              </h2>
              
              <h3 className="font-bold text-gray-900 mb-3">Personal Information</h3>
              <p className="text-gray-700 mb-3">When you place an order or interact with our website, we may collect:</p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Full name
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Email address
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Phone number
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Shipping and billing address
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Payment information (processed securely through Razorpay)
                </li>
              </ul>

              <h3 className="font-bold text-gray-900 mb-3">Automatically Collected Information</h3>
              <p className="text-gray-700 mb-3">When you browse our website, we may automatically collect:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  IP address and browser type
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Device information (mobile, desktop, tablet)
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Pages visited and time spent on our website
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Referring website or source
                </li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Eye className="w-6 h-6 text-yellow-500 mr-2" />
                How We Use Your Information
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <p className="text-gray-700 mb-3">We use your information to:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Process and fulfill your orders
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Send order confirmations, shipping updates, and delivery notifications
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Respond to your inquiries and provide customer support
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Process refunds and returns
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Send promotional communications (only if you opt-in)
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Improve our website and services
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Prevent fraud and ensure secure transactions
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Comply with legal obligations
                  </li>
                </ul>
              </div>
            </section>

            {/* Payment Security */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Lock className="w-6 h-6 text-yellow-500 mr-2" />
                Payment Security
              </h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    All payment transactions are processed through <strong>Razorpay</strong>, a PCI-DSS compliant payment gateway.
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    We do <strong>NOT</strong> store your credit card, debit card, or bank account details on our servers.
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    All data transmission is encrypted using SSL (Secure Socket Layer) technology.
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Your payment information is handled directly by Razorpay under their privacy policy.
                  </li>
                </ul>
              </div>
            </section>

            {/* Information Sharing */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Information Sharing & Disclosure</h2>
              <p className="text-gray-700 mb-4">
                We do <strong>NOT</strong> sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <strong>Service Providers:</strong> With trusted partners who assist in operating our business (payment processors, shipping companies, email services) under strict confidentiality agreements.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <strong>Legal Requirements:</strong> When required by law, court order, or government authority.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.
                </li>
              </ul>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Cookies & Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar technologies to enhance your browsing experience:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <strong>Essential Cookies:</strong> Required for website functionality (e.g., shopping cart, login sessions).
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <strong>Marketing Cookies:</strong> Used to show relevant advertisements (only with your consent).
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                You can control cookie settings through your browser. Disabling cookies may affect certain website features.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, comply with legal obligations, resolve disputes, and enforce our agreements. Order-related data is typically retained for 7 years as per Indian tax and accounting regulations.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <UserCheck className="w-6 h-6 text-yellow-500 mr-2" />
                Your Rights
              </h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-6">
                <p className="text-gray-700 mb-3">You have the right to:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    <strong>Access:</strong> Request a copy of your personal data we hold.
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    <strong>Correction:</strong> Request correction of inaccurate or incomplete data.
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    <strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements).
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    <strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time.
                  </li>
                </ul>
                <p className="text-gray-700 mt-4">
                  To exercise these rights, contact us at jainwatchcompany1975@gmail.com.
                </p>
              </div>
            </section>

            {/* Third-Party Links */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies before providing any personal information.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website is not intended for children under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us immediately, and we will take steps to delete such information.
              </p>
            </section>

            {/* Policy Updates */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The updated policy will be posted on this page with a revised "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            {/* Grievance Officer */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Grievance Officer</h2>
              <p className="text-gray-700 mb-4">
                In accordance with the Information Technology Act, 2000 and rules made thereunder, the Grievance Officer for the purpose of this Privacy Policy is:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700"><strong>Name:</strong> Jain Watch Company</p>
                <p className="text-gray-700"><strong>Email:</strong> jainwatchcompany1975@gmail.com</p>
                <p className="text-gray-700"><strong>Phone:</strong> +91 9927058909</p>
                <p className="text-gray-700 mt-2">Grievances will be addressed within 30 days of receipt.</p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-gray-900 text-white rounded-lg p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Contact Us</h2>
              <p className="text-gray-300 mb-4">For any privacy-related questions or concerns, please contact:</p>
              <div className="space-y-3">
                <p className="text-white font-semibold">Jain Watch Company</p>
                <p className="text-gray-300">Ashoka Market, Street No. 4, Khatauli, Dist. Muzaffarnagar, UP - 251201</p>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-yellow-500 mr-3" />
                  <a href="mailto:jainwatchcompany1975@gmail.com" className="text-yellow-500 hover:underline">
                    jainwatchcompany1975@gmail.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-yellow-500 mr-3" />
                  <a href="tel:9927058909" className="text-yellow-500 hover:underline">
                    +91 9927058909
                  </a>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
